import { useState } from "react";
import { Button, Card, DataList, PageHeader, Sheet, StatusBadge, TextAreaField, type DataColumn } from "@nahal/ui";
import type { ProvisioningRequest } from "@nahal/api-client";
import { useDecide, useRequests } from "../../lib/queries";

export function QueueScreen() {
  const requests = useRequests("PENDING");
  const decide = useDecide();
  const [active, setActive] = useState<ProvisioningRequest | null>(null);
  const [note, setNote] = useState("");

  const columns: Array<DataColumn<ProvisioningRequest>> = [
    { key: "org", header: "Customer", primary: true, cell: (row) => row.organizationName },
    { key: "kind", header: "Request", cell: (row) => row.kind.toLowerCase() },
    { key: "status", header: "Status", cell: (row) => <StatusBadge status={row.status} /> },
    {
      key: "asked",
      header: "Asked",
      desktopOnly: true,
      cell: (row) => new Date(row.createdAt).toLocaleString(),
    },
  ];

  function act(decision: "approve" | "reject") {
    if (!active) return;
    decide.mutate(
      { requestId: active.id, decision, note },
      {
        onSuccess: () => {
          setActive(null);
          setNote("");
        },
      },
    );
  }

  return (
    <>
      <PageHeader title="Provisioning queue" description="What customers are waiting on." />

      <Card>
        <DataList
          columns={columns}
          rows={requests.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={requests.isLoading}
          error={requests.isError ? "Could not load the queue." : null}
          empty={{ title: "Queue is clear", description: "Nothing is waiting on us." }}
          onRowClick={(row) => setActive(row)}
        />
      </Card>

      <Sheet
        open={active !== null}
        title={active ? `${active.kind.toLowerCase()} for ${active.organizationName}` : ""}
        description={active?.note ?? undefined}
        onClose={() => setActive(null)}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={() => setActive(null)}>
              Close
            </Button>
            <Button variant="danger" size="lg" onClick={() => act("reject")} disabled={decide.isPending}>
              Reject
            </Button>
            <Button variant="solid" size="lg" onClick={() => act("approve")} disabled={decide.isPending}>
              Approve
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {active?.payload && (
            <pre className="overflow-x-auto rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-secondary)]">
              {active.payload}
            </pre>
          )}
          <TextAreaField
            label="Decision note"
            hint="The customer sees this."
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </Sheet>
    </>
  );
}
